import { WidgetType } from 'types/widget.types';
import { isDevelopment } from 'utils/environment';
import { isFiniteNumber } from 'utils/number';
import { HalfPieChartWidgetData } from 'widgets/ChartWidgets/HalfPieChartWidget/HalfPieChartWidget.types';

import { HighchartsSeriesData } from '../ChartWidget.hooks';
import { HighchartsOptions } from '../DataTableChartCommon/DataTableChartCommon.types';
import { getHighChartsPieConfig } from '../PieChartWidget/PieChartWidget.helpers';
import {
  DEFAULT_HALF_DONUT_PIE_INNER_SIZE,
  DEFAULT_HALF_PIE_CHART_DATA,
  DEFAULT_HEIGHT,
  DEFAULT_PIE_SIZE,
  DEFAULT_WIDTH,
  MAX_PIE_SIZE,
} from './HalfPieChartWidget.config';
import { VERSION } from './HalfPieChartWidget.upgrade';

/**
 * Generate default PieChartWidget data
 */
export const generateDefaultData = () => {
  return {
    widgetType: WidgetType.HalfPieChart,
    widgetData: {
      version: VERSION,
      widthPx: DEFAULT_WIDTH,
      heightPx: DEFAULT_HEIGHT,
      topPx: 0,
      leftPx: 0,
      rotateDeg: 0,
      altText: '',
      isDecorative: false,
      patterns: {
        enabled: false,
        list: [],
      },
      ...DEFAULT_HALF_PIE_CHART_DATA,
    } as HalfPieChartWidgetData,
  };
};

/**
 * Generate default DonutPieChartWidget data
 */
export const generateHalfDonutPieDefaultData = () => {
  const defaultData = generateDefaultData();

  return {
    ...defaultData,
    widgetData: {
      ...defaultData.widgetData,
      generalOptions: {
        ...defaultData.widgetData.generalOptions,
        innerSize: DEFAULT_HALF_DONUT_PIE_INNER_SIZE,
      },
    },
  };
};

/**
 * A helper functio to calculate the half pie/donut size that is copied from the E1 code
 * @see assets halfpiev2.standard.widget.js
 *
 * @param widthPx widget width in px
 * @param heightPx widget height in px
 * @returns A size string used in the Highcharts config
 */
export const getPieSize = (widthPx: number, heightPx: number) => {
  let pieSize = DEFAULT_PIE_SIZE;

  // Only apply the size calculation if the width and height are valid
  if (isFiniteNumber(heightPx) && heightPx > 0 && isFiniteNumber(widthPx) && widthPx > 0) {
    // height to width ratio times 100
    const percentage = heightPx * (100 / widthPx);

    // If the height is greater than the width, then the pie size is 100%
    // Otherwise, not sure the logic here from the e1 code
    pieSize = percentage > 100 ? 100 : 100 + (100 - percentage) * 3;

    // Limit the pie size to the MAX_PIE_SIZE
    pieSize = Math.min(pieSize, MAX_PIE_SIZE);
  }

  return `${pieSize}%`;
};

/**
 * @param defaultOptions Is modified by reference! Ensure that you're always passing a new object.
 * @param widgetData
 * @param categories
 * @param series
 * @param keyCustom
 */
export const getHighChartsHalfPieConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: HalfPieChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const { widthPx, heightPx } = widgetData;
  const options = getHighChartsPieConfig(defaultOptions, widgetData, categories, series, keyCustom);

  const topPosition = widgetData.legend.enabled ? '110%' : '100%';

  if (options.plotOptions?.pie !== undefined) {
    options.plotOptions.pie.center = [null, topPosition];
    options.plotOptions.pie.size = getPieSize(widthPx, heightPx);
    options.plotOptions.pie.startAngle = -90;
    options.plotOptions.pie.endAngle = 90;
  } else {
    if (isDevelopment) {
      throw new Error('plotOptions.pie is required for HalfPieChartWidget');
    }
  }

  return options;
};
